import { type FunctionComponent, type PropsWithChildren, createContext, useContext, useState } from 'react';
import { type StoreApi, useStore } from 'zustand';
import { doesWindowExist } from '../../utils/dom/dom.utils';

/**
 * True when code is executing inside a Jest test, in either the jsdom or node environment.
 */
const isJestRunning = () =>
	doesWindowExist() ? Boolean(window.isJestRunning) : typeof process !== 'undefined' && process.env.JEST_WORKER_ID !== undefined;

/**
 * A factory that builds a vanilla zustand store, optionally seeded with initial state.
 * Must be created with `createStore` from 'zustand' (NOT `create`), so that a fresh
 * instance can be made for every mounted provider.
 */
export type StoreFactory<TState, TInitialState> = (initialState?: TInitialState) => StoreApi<TState>;

export type StoreProviderProps<TInitialState> = PropsWithChildren<{
	initialState?: TInitialState;
}>;

export type StoreContextOptions = {
	/**
	 * By default, using the store without a Provider above it throws. Set this to true to
	 * fall back to the factory's default state instead: reads work, writes are ignored.
	 * A warning is logged once per store (never under Jest) so these spots can be found
	 * and wrapped over time.
	 */
	allowMissingProvider?: boolean;
};

export type StoreContext<TState, TInitialState> = {
	Provider: FunctionComponent<StoreProviderProps<TInitialState>>;
	/** Subscribes to a slice of the store. Re-renders only when the selected value changes. */
	useStore: <TSelected>(selector: (state: TState) => TSelected) => TSelected;
};

/**
 * Turns a zustand store factory into a scoped `{ Provider, useStore }` pair.
 */
export function createStoreContext<TState, TInitialState = Partial<TState>>(
	createStoreFn: StoreFactory<TState, TInitialState>,
	/**
	 * The store's name, e.g. 'ScrollLockStore'. Only used for labels: React DevTools names and
	 * the messages below assume the conventional exports `use{Name}` and `<{Name}Provider>`.
	 */
	displayName = 'Store',
	{ allowMissingProvider = false }: StoreContextOptions = {}
): StoreContext<TState, TInitialState> {
	const Context = createContext<StoreApi<TState> | null>(null);
	Context.displayName = `${displayName}Context`;

	const Provider: FunctionComponent<StoreProviderProps<TInitialState>> = ({ initialState, children }) => {
		// Lazy initializer: the factory runs exactly once per mounted provider and the
		// instance survives re-renders of the provider.
		const [store] = useState(() => createStoreFn(initialState));
		return <Context.Provider value={store}>{children}</Context.Provider>;
	};
	Provider.displayName = `${displayName}Provider`;

	// Built lazily, once, only for stores that opt in with `allowMissingProvider`.
	let defaultStore: StoreApi<TState> | null = null;
	const getDefaultStore = (): StoreApi<TState> => {
		if (!defaultStore) {
			const state = createStoreFn().getState();
			defaultStore = {
				getState: () => state,
				getInitialState: () => state,
				setState: () => {},
				subscribe: () => () => {}
			};
		}
		return defaultStore;
	};

	let warnedMissingProvider = false;
	const warnMissingProvider = () => {
		// Jest fails tests on console output, so the warning is silenced there.
		// Warns once per store definition
		if (warnedMissingProvider || isJestRunning()) {
			return;
		}
		warnedMissingProvider = true;
		// eslint-disable-next-line no-console
		console.warn(
			`[${displayName}] use${displayName} was called without a <${displayName}Provider> above it. ` +
				'Falling back to the default state; writes are ignored. This is for backwards compatibility only, wrap the tree in the provider.'
		);
	};

	const useBoundStore = <TSelected,>(selector: (state: TState) => TSelected): TSelected => {
		const providedStore = useContext(Context);
		if (!providedStore && !allowMissingProvider) {
			throw new Error(`use${displayName} was called outside of its provider. Wrap the tree in <${displayName}Provider>.`);
		}
		if (!providedStore) {
			warnMissingProvider();
		}
		// thin wrapper around react's useSyncExternalStore
		return useStore(providedStore ?? getDefaultStore(), selector);
	};

	return { Provider, useStore: useBoundStore };
}
